/**
 * Fête de signature, reprise de la maquette (`fanfare()`, `confetti()`) : une fanfare jouée avec
 * Web Audio et une pluie de confettis sur un canvas.
 */

type AudioContextCtor = typeof AudioContext;

/** Fanfare (Web Audio). Silencieuse si le navigateur refuse le son (aucune interaction préalable). */
export function playFanfare(): void {
  try {
    const Ctor: AudioContextCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (!Ctor) return;
    const C = new Ctor();
    const notes = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5, 1318.51];
    const at = [0, 0.11, 0.22, 0.33, 0.5, 0.6, 0.72];
    notes.forEach((f, i) => {
      const o = C.createOscillator();
      const g = C.createGain();
      const t = C.currentTime + (at[i] ?? 0);
      const len = i === notes.length - 1 ? 1.1 : 0.26;
      o.type = 'triangle';
      o.frequency.value = f;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.22, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + len);
      o.connect(g);
      g.connect(C.destination);
      o.start(t);
      o.stop(t + len + 0.05);
    });
    for (let k = 0; k < 14; k++) {
      const o = C.createOscillator();
      const g = C.createGain();
      const t = C.currentTime + 0.75 + k * 0.045;
      o.type = 'sine';
      o.frequency.value = 1800 + Math.random() * 1800;
      g.gain.setValueAtTime(0.06, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      o.connect(g);
      g.connect(C.destination);
      o.start(t);
      o.stop(t + 0.15);
    }
    // Libère le contexte audio une fois la fanfare jouée.
    setTimeout(() => void C.close().catch(() => undefined), 2500);
  } catch {
    /* son indisponible */
  }
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Confettis sur `canvas` pendant 3,2 s, tant que `isOpen()` reste vrai. */
export function runConfetti(canvas: HTMLCanvasElement, isOpen: () => boolean): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = (canvas.width = window.innerWidth);
  const H = (canvas.height = window.innerHeight);
  const cols = ['#FB8500', '#FFB703', '#219EBC', '#8ECAE6', '#3DBF7C', '#E07BC9', '#7B98FF'];
  const P = Array.from({ length: 170 }, () => ({
    x: W / 2 + (Math.random() - 0.5) * 120,
    y: H * 0.45,
    vx: (Math.random() - 0.5) * 16,
    vy: -Math.random() * 15 - 5,
    r: Math.random() * 6 + 4,
    c: cols[Math.floor(Math.random() * cols.length)] ?? '#FB8500',
    a: Math.random() * 6,
    va: (Math.random() - 0.5) * 0.3,
  }));
  const t0 = performance.now();
  const frame = (now: number) => {
    ctx.clearRect(0, 0, W, H);
    for (const p of P) {
      p.vy += 0.35;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.a += p.va;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.a);
      ctx.fillStyle = p.c;
      ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
      ctx.restore();
    }
    if (now - t0 < 3200 && isOpen()) requestAnimationFrame(frame);
    else ctx.clearRect(0, 0, W, H);
  };
  frame(t0);
}
